import { NSARouterMakerResult } from 'namirasoft-account-react'
import { UserFullRow } from 'namirasoft-password'
import { NSBox, NSButton, NSButtonBlue, NSLabel, NSLoading, NSPanel, NSRow } from 'namirasoft-site-react'
import { Component, createRef } from 'react'
import QuestionsForm from '../components/QuestionsForm'
import QuestionsSummary from '../components/QuestionsSummary'
import { PasswordLayout } from '../PasswordLayout'
import { PasswordRouterProps } from '../PasswordRouterProps'
import { PasswordQuestion } from '../PasswordQuestion'
import { Storage } from '../Storage'
import Styles from './SetupWizard.module.css'

export interface QuestionsChangePageProps extends NSARouterMakerResult, PasswordRouterProps
{
    user: UserFullRow;
    loadUser: () => Promise<void>;
}

export interface QuestionsChangePageState
{
    loading: boolean;
    questions: string[] | null;
    step: number;
    finished: null | { questions: string[], answers: string[] };
}

class QuestionsChangePage extends Component<QuestionsChangePageProps, QuestionsChangePageState>
{
    private static GROUP_COUNT = QuestionsForm.GROUP_COUNT;
    private QuestionsForm_Ref = createRef<QuestionsForm>();

    constructor(props: QuestionsChangePageProps)
    {
        super(props);
        this.state = {
            loading: true,
            questions: null,
            step: 1,
            finished: null,
        };
        this.prevStep = this.prevStep.bind(this);
        this.apply = this.apply.bind(this);
    }

    override componentDidMount(): void
    {
        this.loadQuestions();
    }

    async loadQuestions()
    {
        try
        {
            let response = await this.props.servers.password.question.List();
            this.setState(() => ({ questions: response.questions, loading: false }));
        }
        catch
        {
            // The server already reported the error through the notifier.
            this.setState(() => ({ loading: false }));
        }
    }

    nextStep()
    {
        if (this.state.step < QuestionsChangePage.GROUP_COUNT)
            this.setState((prev) => ({ step: prev.step + 1 }));
    }

    prevStep()
    {
        if (this.state.step > 1)
            this.setState((prev) => ({ step: prev.step - 1 }));
    }

    async apply(onFinished: () => void)
    {
        let questions: string[];
        let answers: string[];
        let answers_ciphered: string[];
        try
        {
            this.QuestionsForm_Ref.current?.getGroupValue(this.state.step - 1, true);

            if (this.state.step < QuestionsChangePage.GROUP_COUNT)
            {
                this.nextStep();
                onFinished();
                return;
            }

            let master_key = await Storage.getMasterKey(this.props.user.id);
            if (!master_key)
            {
                this.props.notifier.onError("Please verify your password first.");
                onFinished();
                return;
            }

            ({ questions, answers } = this.QuestionsForm_Ref.current?.getValue() ?? { questions: [], answers: [] });
            answers_ciphered = await PasswordQuestion.buildAnswerCiphers(answers, master_key);
        }
        catch (error)
        {
            // Local input/encryption errors are not reported by the server, so notify here.
            this.props.notifier.onError(error as Error);
            onFinished();
            return;
        }

        try
        {
            await this.props.servers.password.user.Setup({
                questions: questions,
                cipher: this.props.user.cipher,
                ciphers: answers_ciphered,
            });

            Storage.delUser(this.props.user.id);
            this.props.notifier.onSuccess("Your questions and answers have been updated successfully.");
            this.setState(() => ({ finished: { questions, answers } }));
            onFinished();
        }
        catch
        {
            // The server already reported the error through the notifier.
            onFinished();
        }
    }

    override render()
    {
        return (
            <PasswordLayout
                {...this.props}
                action={{
                    title: "Change Questions",
                    description: "After setup, replace your current security questions and answers with a new set, while you still have access to your vault."
                }}
            >
                <>
                    {
                        this.state.loading &&
                        <NSLoading />
                    }
                    {
                        !this.state.loading && this.state.questions && !this.state.finished &&
                        <NSPanel classList={["mx-auto"]}>
                            <NSLabel
                                title={`Step ${this.state.step} / ${QuestionsChangePage.GROUP_COUNT}`}
                                description="You can change any question and you must re-enter every answer. The answers are not stored and cannot be shown here for your security."
                                style={{ width: NSBox.width.triple }}
                            />
                            <QuestionsForm
                                ref={this.QuestionsForm_Ref}
                                {...this.props}
                                questions={this.state.questions}
                                visibleGroup={this.state.step - 1}
                            />
                            <NSRow style={{ position: "relative" }}>
                                <NSButtonBlue
                                    title={this.state.step === QuestionsChangePage.GROUP_COUNT ? "Apply" : "Next"}
                                    onClick={{
                                        action: this.apply,
                                        showLoading: this.state.step === QuestionsChangePage.GROUP_COUNT
                                    }}
                                />
                                {
                                    this.state.step > 1 &&
                                    <NSButton
                                        title=""
                                        icon={{ src: "https://static.namirasoft.com/image/concept/arrow/left/white-blue.svg" }}
                                        onClick={{ showLoading: false, action: this.prevStep }}
                                        classList={[Styles.ns_arrow_button]}
                                    />
                                }
                            </NSRow>
                        </NSPanel>
                    }
                    {
                        this.state.finished &&
                        <NSPanel classList={["mx-auto"]}>
                            <QuestionsSummary
                                {...this.props}
                                questions={this.state.finished.questions}
                                answers={this.state.finished.answers}
                                submitTitle="Go to Vault"
                                loadUser={async () =>
                                {
                                    await this.props.loadUser();
                                    this.props.url.redirect("/", {});
                                }}
                            />
                        </NSPanel>
                    }
                </>
            </PasswordLayout>
        )
    }
}

export default QuestionsChangePage;